import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from "../../api/auth";
import { affiliationsAPI } from '../../api/affiliations';
import styles from './Register.module.css';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [affiliations, setAffiliations] = useState([]);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    password: '',
    confirmPassword: '',
    affiliation: '',
    bio: '',
    role: 'AUTHOR',
    agreeToPolicy: false
  });

  // Загрузка списка affiliations при монтировании компонента
  useEffect(() => {
    const fetchAffiliations = async () => {
      try {
        const data = await affiliationsAPI.getAll();
        setAffiliations(data);
      } catch (err) {
        console.error('Failed to load affiliations:', err);
        setError('Failed to load affiliations list');
      }
    };

    fetchAffiliations();
  }, []);

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    // Очистка ошибки при изменении формы
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.country) {
      setError('Please select a country');
      return false;
    }
    if (!formData.affiliation) {
      setError('Please select an affiliation');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.agreeToPolicy) {
      setError('You must agree to the policy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Формируем данные для отправки согласно API
      const registrationData = {
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        first_name: formData.firstName,
        last_name: formData.lastName,
        role: formData.role,
        country: formData.country,
        affiliation: formData.affiliation,
        bio: formData.bio
      };

      const response = await authAPI.register(registrationData);
      
      console.log('Registration successful:', response);
      
      // Перенаправление на главную страницу или dashboard после успешной регистрации
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftSection}>
        <div className={styles.formWrapper}>
          <h1 className={styles.title}>Register</h1>
          
          <form onSubmit={handleSubmit}>
            {error && (
              <div className={styles.error}>
                {error}
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>First name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={styles.input}
                  placeholder="Enter first name"
                  disabled={loading}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Last name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={styles.input}
                  placeholder="Enter last name"
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={styles.input}
                placeholder="Enter email address"
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Country</label>
              <select
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={styles.input}
                disabled={loading}
              >
                <option value="">Select country</option>
                <option value="Uzbekistan">Uzbekistan</option>
                <option value="Germany">Germany</option>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Affiliation</label>
              <select
                value={formData.affiliation}
                onChange={(e) => handleChange('affiliation', e.target.value)}
                className={styles.input}
                disabled={loading}
              >
                <option value="">Select affiliation</option>
                {affiliations.map((aff) => (
                  <option key={aff.id} value={aff.name}>
                    {aff.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={styles.input}
                placeholder="Enter password"
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Confirm password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={styles.input}
                placeholder="Confirm password"
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                className={styles.textarea}
                placeholder="Tell us about yourself"
                rows="4"
                disabled={loading}
              />
            </div>

            <div className={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="policy"
                checked={formData.agreeToPolicy}
                onChange={(e) => handleChange('agreeToPolicy', e.target.checked)}
                className={styles.checkbox}
                disabled={loading}
              />
              <label htmlFor="policy" className={styles.checkboxLabel}>
                I agree to the <a href="#" className={styles.link}>policy & terms</a>
              </label>
            </div>

            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <div className={styles.footer}>
              Have an account? <Link to="/signin" className={styles.link}>Sign in</Link>
            </div>
          </form>
        </div>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.imageOverlay}></div>
      </div>
    </div>
  );
};

export default Register;